import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import palette from '@theme/palette';
import { useTranslation } from 'react-i18next';
import { useBlobImage } from '@entities/solution';
function ScaledImage({ imageKey, alt }) {
    const { t } = useTranslation();
    const { url, isLoading, isError } = useBlobImage(imageKey);
    if (isLoading)
        return _jsx(CircularProgress, { size: 28 });
    if (!url) {
        return (_jsx(Typography, { sx: { fontSize: '14px', color: palette.secondary.text }, children: isError ? t('Could not load image') : t('Image not found') }));
    }
    return (_jsx("img", { src: url, alt: alt, style: {
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block'
        } }));
}
function Thumbnail({ item, selected, onClick }) {
    const { url, isLoading } = useBlobImage(item.imageKey);
    return (_jsx(Box, { onClick: onClick, title: item.label, sx: {
            flex: '0 0 auto',
            width: 76,
            height: 58,
            borderRadius: '6px',
            border: selected ? `2px solid ${palette.primary.main}` : `1px solid ${palette.neutral[90]}`,
            overflow: 'hidden',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: palette.grey[50]
        }, children: isLoading || !url ? (_jsx(CircularProgress, { size: 16 })) : (_jsx("img", { src: url, alt: item.label, style: { width: '100%', height: '100%', objectFit: 'cover' } })) }));
}
export function ImageGallery({ items, height = 'clamp(320px, 46vh, 560px)', renderActions }) {
    var _a;
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState((_a = items[0]) === null || _a === void 0 ? void 0 : _a.id);
    useEffect(() => {
        if (items.length > 0 && !items.some(item => item.id === selectedId)) {
            setSelectedId(items[0].id);
        }
    }, [items, selectedId]);
    if (items.length === 0) {
        return (_jsx(Box, { display: "flex", justifyContent: "center", alignItems: "center", height: 220, sx: {
                bgcolor: palette.grey[50],
                borderRadius: '8px',
                border: `1px solid ${palette.neutral[95]}`
            }, children: _jsx(Typography, { sx: { fontSize: '14px', color: palette.secondary.text }, children: t('No images available') }) }));
    }
    const selected = items.find(item => item.id === selectedId) || items[0];
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: {
                    position: 'relative',
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: palette.grey[50],
                    borderRadius: '8px',
                    border: `1px solid ${palette.neutral[95]}`,
                    overflow: 'hidden',
                    p: 1
                }, children: [_jsx(ScaledImage, { imageKey: selected.imageKey, alt: selected.label }, selected.id), renderActions && (_jsx(Box, { sx: { position: 'absolute', top: 8, right: 8 }, children: renderActions(selected) }))] }), _jsx(Typography, { sx: {
                    fontSize: '13px',
                    fontWeight: 500,
                    color: palette.neutral[30],
                    mt: 1,
                    textAlign: 'center'
                }, children: selected.label }), items.length > 1 && (_jsx(Stack, { direction: "row", spacing: 1, sx: { mt: 1, overflowX: 'auto', pb: 1, justifyContent: 'center' }, children: items.map(item => (_jsx(Thumbnail, { item: item, selected: item.id === selected.id, onClick: () => setSelectedId(item.id) }, item.id))) }))] }));
}
